import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, Alert, CircularProgress,
  FormControl, InputLabel, Select, MenuItem, TextField, Stack,
  Breadcrumbs, Link as MuiLink, Chip,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

interface AssessmentOption {
  id: number;
  title: string;
  due_date: string;
  submission_format: string | null;
  max_points: number;
}

const LearnerSubmitPage: React.FC = () => {
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState<AssessmentOption[]>([]);
  const [selectedAssessmentId, setSelectedAssessmentId] = useState<number | ''>('');
  const [file, setFile] = useState<File | null>(null);
  const [textContent, setTextContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchingAssessments, setFetchingAssessments] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    const fetchAssessments = async () => {
      try {
        const response = await axios.get('/api/assignments/', { params: { page_size: 200 } });
        const results = response.data.results || [];
        setAssessments(results.map((a: any) => ({
          id: a.id,
          title: a.title,
          due_date: a.due_date,
          submission_format: a.submission_format || 'file',
          max_points: a.max_points,
        })));
      } catch (err: any) {
        console.error('Error fetching assessments:', err);
        setError('Failed to load assessments.');
      } finally {
        setFetchingAssessments(false);
      }
    };
    fetchAssessments();
  }, []);

  const selectedAssessment = assessments.find(a => a.id === selectedAssessmentId) || null;
  const format = selectedAssessment?.submission_format || 'file';

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleSubmit = async () => {
    if (!selectedAssessmentId) {
      setError('Please select an assessment.');
      return;
    }

    setError(null);
    setSuccess(false);
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('assessment_id', String(selectedAssessmentId));

      if (file) {
        formData.append('file', file);
      }
      if (textContent.trim()) {
        formData.append('text_content', textContent.trim());
      }

      // Send dummy values for required serializer fields - backend perform_create overrides these
      if (!file && !textContent.trim()) {
        setError('Please provide a file or text content.');
        setLoading(false);
        return;
      }
      formData.append('file_path', 'pending');
      formData.append('original_filename', file ? file.name : 'text_submission.txt');
      formData.append('content_type', 'text');

      await axios.post('/api/submissions/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setSuccess(true);
      setFile(null);
      setTextContent('');
      setSelectedAssessmentId('');
    } catch (err: any) {
      console.error('Error submitting:', err);
      const detail = err.response?.data?.detail || err.response?.data?.non_field_errors?.[0];
      if (detail) {
        setError(typeof detail === 'string' ? detail : JSON.stringify(detail));
      } else if (err.response?.data) {
        // Try to extract field errors
        const fieldErrors = Object.entries(err.response.data)
          .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
          .join('; ');
        setError(fieldErrors || 'Submission failed. Please try again.');
      } else {
        setError('Submission failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDueDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  const formatLabel = (fmt: string) => {
    switch (fmt) {
      case 'file': return 'File Upload';
      case 'text': return 'Text/Code Paste';
      case 'file_text': return 'File and Text';
      default: return fmt;
    }
  };

  if (fetchingAssessments) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
        <MuiLink component="button" variant="body1" onClick={() => navigate('/dashboard')} sx={{ cursor: 'pointer', textDecoration: 'none' }}>
          Dashboard
        </MuiLink>
        <Typography color="text.primary">Submit Work</Typography>
      </Breadcrumbs>

      <Box mb={3}>
        <Typography variant="h4" gutterBottom>Submit Work</Typography>
        <Typography variant="body2" color="textSecondary">
          Select an assessment and upload your submission
        </Typography>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 3 }}>Submission uploaded successfully!</Alert>}
      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper sx={{ p: 4 }}>
        <Stack spacing={3}>
          <FormControl fullWidth>
            <InputLabel>Assessment</InputLabel>
            <Select
              value={selectedAssessmentId}
              onChange={(e) => {
                setSelectedAssessmentId(e.target.value as number);
                setFile(null);
                setTextContent('');
                setError(null);
                setSuccess(false);
              }}
              label="Assessment"
            >
              <MenuItem value="">
                <em>Select an assessment...</em>
              </MenuItem>
              {assessments.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <span>{a.title}</span>
                    <Chip label={formatLabel(a.submission_format || 'file')} size="small" variant="outlined" />
                    <Typography variant="caption" color="textSecondary" sx={{ ml: 'auto' }}>
                      Due: {formatDueDate(a.due_date)}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedAssessment && (
            <Alert severity="info" variant="outlined">
              <strong>{selectedAssessment.title}</strong> &mdash; {selectedAssessment.max_points} points
              <br />
              Format: {formatLabel(format)} | Due: {formatDueDate(selectedAssessment.due_date)}
            </Alert>
          )}

          {/* File upload area */}
          {selectedAssessment && (format === 'file' || format === 'file_text') && (
            <Box
              onDragEnter={handleDrag}
              onDragOver={handleDrag}
              onDragLeave={handleDrag}
              onDrop={handleDrop}
              sx={{
                border: '2px dashed',
                borderColor: dragActive ? 'primary.main' : 'grey.400',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                bgcolor: dragActive ? 'action.hover' : 'background.default',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onClick={() => document.getElementById('file-input')?.click()}
            >
              <input
                id="file-input"
                type="file"
                hidden
                onChange={handleFileChange}
              />
              <CloudUploadIcon sx={{ fontSize: 48, color: 'grey.500', mb: 1 }} />
              <Typography variant="body1" gutterBottom>
                {file ? file.name : 'Drag & drop your file here, or click to browse'}
              </Typography>
              {file && (
                <Typography variant="caption" color="textSecondary">
                  {(file.size / 1024).toFixed(1)} KB
                </Typography>
              )}
            </Box>
          )}

          {/* Text area */}
          {selectedAssessment && (format === 'text' || format === 'file_text') && (
            <TextField
              fullWidth
              multiline
              rows={12}
              label="Paste your text or code here"
              value={textContent}
              onChange={(e) => setTextContent(e.target.value)}
              placeholder="Paste your code, essay, or text submission here..."
              sx={{
                '& .MuiInputBase-input': {
                  fontFamily: format === 'text' ? 'monospace' : 'inherit',
                },
              }}
            />
          )}

          {selectedAssessment && (
            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                size="large"
              >
                {loading ? 'Submitting...' : 'Submit'}
              </Button>
            </Box>
          )}
        </Stack>
      </Paper>
    </Box>
  );
};

export default LearnerSubmitPage;
